import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues, useFormikContext } from 'formik';
import * as yup from 'yup';

import { updateUserIsActive } from 'actions/adminTool';
import Header from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import SelectModal from 'components/Selects/BookingSelectDate/components/Options';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import ToggleCard from 'components/ToggleCard';
import useToast from 'hooks/useToast';
import { useSelector } from 'react-redux';

import { Container, Wrapper, FormContainer, Field } from './styles';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { AdminUser } from 'models/adminUser';
import { RootState } from 'store';

const AdminToolUpdateUser = () => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const typeRef = useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<AdminUser>();
  const [isRegisteringNetSuite, setIsRegisteringNetSuite] = useState(false);
  const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration.configuration);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      await updateUserIsActive(values.email, values.isActive, values.registerUserNetSuite, values.type, values.username, values.password);
      toast.add({ type: 'success', message: t('adminTool.updateUserForm.toastSuccess') });
    } catch (error) {
      toast.add({ type: 'danger', message: ` ${error.toString()} - ${error?.response?.data?.errors[0]} `});
    }

    setIsLoading(false);
  };

  const containsSequentialNumbers = (str: string): boolean => {
    for (let i = 0; i < str.length - 1; i++) {
      const current = parseInt(str[i]);
      const next = parseInt(str[i + 1]);

      if (!isNaN(current) && !isNaN(next)) {
        if (next === current + 1 || next === current - 1) {
          return true;
        }
      }
    }
    return false;
  };

  const validationSchema = yup.object().shape({
    email: yup.string().required(),
    isActive: yup.boolean().required(),
    registerUserNetSuite: yup.boolean().required(),
    type: yup.number(),
    username: yup.string().when('registerUserNetSuite', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired()
    }),
    password: yup.string().when('registerUserNetSuite', {
      is: true,
      then: (schema) =>
        schema.test('password-validation', 'Senha inválida', function (value) {
          const password = value || '';
          const hasLength = password.length >= 8 && password.length <= 12;
          const hasSpecialChar = /[!¡”#$%/,*.@&()\-_=+{}\[\]|':;¿?<>\^]/.test(password);
          const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
          const hasNumber = /\d/.test(password);
          const hasNoConsecutive = !/(\d)\1{1,}/.test(password);
          const hasNoSequentialNumbers = !containsSequentialNumbers(password);
          const hasUserInfo = validatePasswordContainsUserInfo(password);

          const isSsoCorporate = companyConfiguration?.loginSso?.isSsoCorporate ?? false;

          if (isSsoCorporate) {
            return hasLength && hasUpperLower && hasNumber;
          }

          return (
            hasLength &&
            hasSpecialChar &&
            hasUpperLower &&
            hasNumber &&
            hasNoConsecutive &&
            hasNoSequentialNumbers &&
            hasUserInfo
          );
        }),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const initValues = {
    email: '',
    isActive: true,
    registerUserNetSuite: false,
    type: 1,
    username: '',
    password: '',
  };

  const types = [
    { text: 'Admin (0)', value: 0 },
    { text: 'Traveler (1)', value: 1 },
    { text: 'Guest (2)', value: 2 },
    { text: 'Invited (3)', value: 3 },
    { text: 'SuperAdmin (4)', value: 4 },
  ];

  const validatePassword = (password: string) => {
    const specialChars = /[!¡”#$%/,*.@&()\-_=+{}\[\]|':;¿?<>\^`]/;
    const isEmpty = password.trim().length === 0;
    const isSsoCorporate = companyConfiguration?.loginSso?.isSsoCorporate ?? false;

    const validations = [
      {
        name: 'length',
        title: t('userForm.accessSystem.length.title'),
        message: t('userForm.accessSystem.length.message'),
        valid: isEmpty ? false : password.length >= 8 && password.length <= 12
      },
      ...(!isSsoCorporate ? [{
        name: 'special-char',
        title: t('userForm.accessSystem.specialChar.title'),
        message: t('userForm.accessSystem.specialChar.message'),
        valid: isEmpty ? false : specialChars.test(password)
      }] : []),
      {
        name: 'uppercase-char',
        title: t('userForm.accessSystem.uppercaseChar.title'),
        message: t('userForm.accessSystem.uppercaseChar.message'),
        valid: isEmpty ? false : /[a-z]/.test(password) && /[A-Z]/.test(password)
      },
      {
        name: 'numeric-char',
        title: t('userForm.accessSystem.numericChar.title'),
        message: t('userForm.accessSystem.numericChar.message'),
        valid: isEmpty ? false : /\d/.test(password)
      },
      ...(!isSsoCorporate ? [
        {
          name: 'consecutive-char',
          title: t('userForm.accessSystem.consecutiveChar.title'),
          message: t('userForm.accessSystem.consecutiveChar.message'),
          valid: isEmpty ? false : (!/(\d)\1{1,}/.test(password) && !containsSequentialNumbers(password))
        },
        {
          name: 'profile-date',
          title: t('userForm.accessSystem.profileData.title'),
          message: t('userForm.accessSystem.profileData.message'),
          valid: isEmpty ? false : validatePasswordContainsUserInfo(password)
        }
      ] : [])
    ];

    return validations;
  };

  const validatePasswordContainsUserInfo = (
    password: string
  ) => {
    
    if (!password) return false;

    // campos para validar
    const userValues = [
      user?.name,
      user?.lastName,
      user?.username,
      user?.email,
      user?.phoneNumber,
      user?.documentNumber,
      user?.passportNumber
    ].filter(Boolean); // Remove null/undefined
    
    const validation = userValues.some((val) => 
      password.sanitize().toLocaleLowerCase().includes(val.sanitize().toLocaleLowerCase())
    );

    return !validation;
  };

  const PasswordValidation = () => {
    const { values } = useFormikContext<{ password: string }>();
    const [rules, setRules] = useState(() => validatePassword(''));
  
    useEffect(() => {
      setRules(validatePassword(values.password || ''));
    }, [values.password]);
  
    return (
      <div style={{ marginTop: 8 }}>
        {values.password && rules.map(rule => (
          <div key={rule.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {rule.valid ? (
              <span style={{ color: 'green' }}>✔</span>
            ) : (
              <span style={{ color: 'red' }}>✖</span>
            )}
            <span style={{ fontSize: '12px', paddingTop: '3px' }}><b>{rule.title}: </b>{rule.message}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Wrapper className="target">
      <>
        <Mobile>
          <Header goBack={() => history.push('/corporate/booking')}>{t('adminTool.updateUserForm.title')}</Header>
        </Mobile>

        <Desktop>
          <ScreenHeader
            title={t('adminTool.updateUserForm.title')}
            subtitle={t('adminTool.updateUserForm.subtitle')}
          />
        </Desktop>

        <Container>
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={(value) => handleSubmit(value)}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            {({ dirty, isSubmitting, isValid, values, setFieldValue }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />
                <FormContainer marginBottom={dirty}>
                  <Section title={t('adminTool.updateUserForm.parameters')} joinContent={false}>
                    <Field name="email" label={t('adminTool.updateUserForm.username')}>
                      <Input />
                    </Field>
                    <Field name="type" label={t('adminTool.updateUserForm.userType')}>
                      <CustomSelect
                        name="name"
                        onChange={(v) => setFieldValue('type', v)}
                        placeHolder="Seleccione el Tipo"
                        selectRef={typeRef}
                        value={types.find((t) => t.value === values.type)?.text ?? ''}
                      >
                        {({ ...selectProps }: CustomSelectRenderProps) => (
                          <SelectModal {...selectProps} selectRef={typeRef} items={types} />
                        )}
                      </CustomSelect>
                    </Field>
                    <ToggleCard
                      title={t('adminTool.updateUserForm.createUserNetsuite')}
                      value={values.registerUserNetSuite}
                      onToggle={(_, value) => {
                        setFieldValue('registerUserNetSuite', value);
                        setIsRegisteringNetSuite(value);
                      }}
                      hide={false}
                    />
                    <ToggleCard
                      title={t('adminTool.updateUserForm.isActive')}
                      value={values.isActive}
                      onToggle={(_, value) => setFieldValue('isActive', value)}
                      hide={false}
                    />
                    {values.registerUserNetSuite && (
                      <>
                        <Field name="username" label={t('adminTool.updateUserForm.username2')}>
                          <Input />
                        </Field>
                        <fieldset>
                          <Field  name="password" label={t('adminTool.updateUserForm.password')}>
                            <Input />
                          </Field>
                          <PasswordValidation />
                        </fieldset>  
                      </>
                    )}
                    
                  </Section>
                  <SubmitButton dirty={true} isEdit loading={isLoading}>
                    {t('adminTool.updateUserForm.applyButton')}
                  </SubmitButton>
                </FormContainer>
              </>
            )}
          </Formik>
        </Container>
      </>
    </Wrapper>
  );
};

export default AdminToolUpdateUser;
